import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import {
  Typography,
  DragAndDropTypesEnum,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { GanttEntryTypesEnum } from '@thinknet-gmbh/platform-common';

import { getEntryColors } from 'src/pages/gantt/gantt-calendar/gantt-view/components/data-layer/data-layer.helpers';

import {
  IconDot,
  IconDotsWrapper,
  DayEntryWrapper,
  StripeNameWrapper,
} from './day-entry-info-drag-row.styles';
import { hoverEntry } from './day-entry-info-drag-row.helpers';
import { IDayEntryInfoDragRow } from './day-entry-info-drag-row.interfaces';

const DayEntryInfoDragRow = ({
  entry,
  moveRow,
  actionAllowed,
  onEntryDrop,
}: IDayEntryInfoDragRow) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const { startDateString, endDateString, source } = entry;
  const { stripeName, name: absenceName } = source;
  const isExtraTime = startDateString === endDateString;

  const [, drop] = useDrop({
    accept: DragAndDropTypesEnum.GANTT_DAY_ENTRY_SORTING,
    hover: (currentElem: any, monitor: any) => {
      if (!isExtraTime || !actionAllowed) return;
      const indexes = hoverEntry({ entry, currentElem, monitor, ref: dropRef });

      if (!indexes) return;

      moveRow(indexes.dragIndex, indexes.hoverIndex);
    },
    drop: onEntryDrop,
  });

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: DragAndDropTypesEnum.GANTT_DAY_ENTRY_SORTING,
    item: entry,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isExtraTime && actionAllowed,
  }));

  dragPreview(drop(dropRef));
  dragPreview(getEmptyImage());
  drag(dragRef);

  const [primaryColor, secondaryColor] = getEntryColors({
    type: GanttEntryTypesEnum.ABSENCE_REASON,
    source,
  });

  return (
    <DayEntryWrapper
      ref={dropRef}
      isDragging={isDragging}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
    >
      <StripeNameWrapper>
        <IconDotsWrapper ref={dragRef} isDisabled={!isExtraTime || !actionAllowed}>
          {[...Array(6)].map((_, index) => (
            <IconDot key={index} secondaryColor={secondaryColor} />
          ))}
        </IconDotsWrapper>
        <Typography fontColor={secondaryColor || PLATFORM_THEME_COLORS.primary.grey}>
          {stripeName || absenceName}
        </Typography>
      </StripeNameWrapper>
      <Typography fontColor={secondaryColor || PLATFORM_THEME_COLORS.grayVariations.dark}>
        {!isExtraTime ? `today: ${entry.plannedEntryHours} hr` : `${entry.plannedEntryHours} hr`}
      </Typography>
    </DayEntryWrapper>
  );
};

export default DayEntryInfoDragRow;
